/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component, inject, Input} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {
  MultiLanguageProperty,
  SubmodelElement,
  SubmodelElementCollection,
  SubmodelElementList,
  SubmodelElementTypes,
} from '@product-passport/dpp/model';
import {PropertyComponent} from '../property/property.component';
import {FileComponent} from '../file/file.component';
import {MultiLanguagePropertyComponent} from '../multi-language-property/multi-language-property.component';
import {TranslateOrFallbackPipe} from '../../../dpp-submodel-field/utils/translate-or-fallback.pipe';

@Component({
  selector: 'app-submodel-element-list',
  templateUrl: './submodel-element-list.component.html',
  styleUrl: './submodel-element-list.component.scss',
  imports: [
    IxModule,
    TranslateModule,
    ReactiveFormsModule,
    PropertyComponent,
    FileComponent,
    MultiLanguagePropertyComponent,
    TranslateOrFallbackPipe,
  ],
})
export class SubmodelElementListComponent {
  private readonly fb = inject(FormBuilder);

  @Input() element!: SubmodelElementList;
  @Input() formArray!: FormArray;

  readonly SubmodelElementTypes = SubmodelElementTypes;

  get controls(): (FormControl | FormGroup)[] {
    return this.formArray.controls as (FormControl | FormGroup)[];
  }

  isFormGroup(control: FormControl | FormGroup): control is FormGroup {
    return control instanceof FormGroup;
  }

  asFormGroup(control: FormControl | FormGroup): FormGroup {
    return control as FormGroup;
  }

  /** Returns the original element data for a given list index (used to get field labels). */
  getItemElement(index: number): SubmodelElementCollection | null {
    const items = this.element.value ?? [];
    return (items[index] ?? items[0] ?? null) as SubmodelElementCollection;
  }

  getCollectionFields(index: number): SubmodelElement[] {
    return this.getItemElement(index)?.value ?? [];
  }

  getFormGroupForField(
    groupControl: FormGroup,
    fieldIdShort: string,
  ): FormGroup {
    return groupControl.get(fieldIdShort) as FormGroup;
  }

  getControlForField(
    groupControl: FormGroup,
    fieldIdShort: string,
  ): FormControl {
    return (groupControl.get(fieldIdShort) ??
      this.fb.control('')) as FormControl;
  }

  addItem(): void {
    const template = (this.element.value ?? [])[0];
    if (!template) {
      this.formArray.push(this.fb.control(''));
      return;
    }
    if (template.modelType === SubmodelElementTypes.SubmodelElementCollection) {
      const controls = this.buildCollectionControls(
        template as SubmodelElementCollection,
      );
      this.formArray.push(this.fb.group(controls));
    } else {
      this.formArray.push(this.fb.control(''));
    }
  }

  private buildCollectionControls(
    collection: SubmodelElementCollection,
  ): Record<string, FormControl> {
    const controls: Record<string, FormControl> = {};
    for (const field of collection.value ?? []) {
      controls[field.idShort ?? ''] = this.buildFieldControl(field);
    }
    return controls;
  }

  private buildFieldControl(field: SubmodelElement): FormControl {
    if (field.modelType !== SubmodelElementTypes.MultiLanguageProperty) {
      return this.fb.control('');
    }
    const langKeys =
      (field as MultiLanguageProperty).value?.map((ls) => ls.language) ?? [];
    const langControls = Object.fromEntries(
      langKeys.map((lang) => [lang, this.fb.control('')]),
    );
    return this.fb.group(langControls) as unknown as FormControl;
  }

  removeItem(index: number): void {
    this.formArray.removeAt(index);
  }
}
